import { Box, Divider, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useMemo } from 'react';
import { selectCreativesMap } from '@/entities/creatives';
import { useAppSelector } from '@/shared/model';
import { Switch } from '@/shared/ui';
import { getOptionColors } from './getOptionColors';
import type { ListProps } from './types';

const List = styled('ul')(() => ({
  margin: 0,
  padding: 0,
  listStyleType: 'none',
  overflow: 'auto',
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  display: 'flex',
  flexDirection: 'column',
}));
const Item = styled('li')(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  backgroundColor: 'white',
  lineHeight: 1.5,
  fontSize: '0.75rem',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),

  '&:hover': {
    backgroundColor: 'rgba(6, 63, 254, 0.12)', // --accent-700
  },
}));
const Image = styled('img')(() => ({
  height: 18,
  width: 18,
  aspectRatio: '1 / 1',
  objectFit: 'contain',
}));

export function SplitLegendMediaList({
  filterQuery,
  options,
  palette,
  onOptionsToggle,
  inactiveOptionsSet,
}: ListProps) {
  const creativesMap = useAppSelector(selectCreativesMap);
  const optionColors = useMemo(() => {
    return getOptionColors(options, palette);
  }, [options, palette]);
  const preparedOptions = useMemo(() => {
    const preparedQuery = filterQuery.toLowerCase();

    return options
      .map((adId) => ({
        value: adId,
        label: creativesMap[adId]?.adName ?? adId,
        thumbnailUrl: creativesMap[adId]?.thumbnailUrl ?? '',
      }))
      .filter((option) => {
        return option.label.toLowerCase().includes(preparedQuery);
      });
  }, [options, filterQuery, creativesMap]);
  const allOptionsChecked = useMemo(
    () => preparedOptions.every((option) => !inactiveOptionsSet.has(option.value)),
    [preparedOptions, inactiveOptionsSet],
  );

  if (preparedOptions.length === 0) {
    return (
      <Typography
        color="text.secondary"
        variant="body2"
        sx={{ my: 2, textAlign: 'center' }}
      >
        Nothing found.
      </Typography>
    );
  }

  const handleOptionToggle =
    (option: string) =>
    (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      onOptionsToggle([option], checked);
    };
  const handleAllOptionsToggle = (
    _: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    onOptionsToggle(
      preparedOptions.map((option) => option.value),
      checked,
    );
  };

  return (
    <List>
      <Item sx={{ '&:hover': { backgroundColor: 'inherit' } }}>
        <Switch checked={allOptionsChecked} onChange={handleAllOptionsToggle} />
        All
      </Item>
      <Divider />
      {preparedOptions.map((option) => {
        return (
          <Item key={option.value}>
            <Switch
              customColor={optionColors.get(option.value)}
              checked={!inactiveOptionsSet.has(option.value)}
              onChange={handleOptionToggle(option.value)}
            />
            {option.thumbnailUrl && <Image src={option.thumbnailUrl} />}
            <Tooltip title={option.label}>
              <Box
                component="span"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {option.label}
              </Box>
            </Tooltip>
          </Item>
        );
      })}
    </List>
  );
}
